import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toPng } from "html-to-image";
import { ArrowLeft, Download, Share2, Lock, LogIn, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../components/AuthProvider";
import { PrimeWrapped } from "../components/PrimeWrapped";

const API = process.env.REACT_APP_BACKEND_URL;

export default function WrappedPage() {
  const nav = useNavigate();
  const { user, loading, login } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    if (loading || !user) return;
    fetch(`${API}/api/users/me/wrapped`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then(setData)
      .catch(() => setErr("Couldn't build your Wrapped."));
  }, [loading, user]);

  const download = async () => {
    if (!cardRef.current || busy) return;
    setBusy(true);
    try {
      const dataUrl = await toPng(cardRef.current, {
        cacheBust: true,
        pixelRatio: 2.5,
        backgroundColor: "#0A090D",
      });
      const link = document.createElement("a");
      link.download = `prime-wrapped-${(data?.user?.name || "you").replace(/\s+/g, "_")}.png`;
      link.href = dataUrl;
      link.click();
      toast.success("Saved to downloads");
    } catch (e) {
      toast.error("Couldn't create image");
    } finally {
      setBusy(false);
    }
  };

  const share = async () => {
    if (!cardRef.current || busy) return;
    setBusy(true);
    try {
      const dataUrl = await toPng(cardRef.current, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: "#0A090D",
      });
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], "prime-wrapped.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: "My Prime Wrapped",
          text: `I'm ranked #${data.wrapped.global_rank} on Celebrity Smash!`,
        });
      } else {
        await navigator.clipboard.writeText(window.location.href);
        toast.success("Link copied — share it anywhere");
      }
    } catch {
      /* user cancelled or unsupported */
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen spotlight grain scanlines pt-24 text-center text-[#FDFBF7]/60">Loading…</div>;
  }

  if (!user) {
    return (
      <div className="min-h-screen spotlight grain scanlines">
        <div className="max-w-md mx-auto px-5 pt-16 pb-nav text-center">
          <div className="inline-flex w-16 h-16 rounded-2xl bg-[#FFD700]/15 border border-[#FFD700]/40 items-center justify-center mb-6">
            <Lock size={26} className="text-[#FFD700]" />
          </div>
          <h1 className="font-display text-5xl leading-[0.9] tracking-tight">Prime Wrapped</h1>
          <p className="mt-3 text-sm text-[#FDFBF7]/70 max-w-xs mx-auto">
            Sign in to unlock your annual Prime Wrapped card — a shareable snapshot of your Oracle year.
          </p>
          <button
            data-testid="wrapped-signin-btn"
            onClick={login}
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#FDFBF7] text-[#0A090D] px-6 py-3 font-bold tracking-wide"
          >
            <LogIn size={16} /> Sign in with Google
          </button>
        </div>
      </div>
    );
  }

  if (err) {
    return <div className="min-h-screen pt-16 text-center text-[#FF107A]">{err}</div>;
  }

  if (!data) {
    return <div className="min-h-screen spotlight grain scanlines pt-24 text-center text-[#FDFBF7]/60">Wrapping your year…</div>;
  }

  const empty = data.wrapped.total_completed === 0;

  return (
    <div className="min-h-screen spotlight grain scanlines">
      <div className="max-w-md mx-auto px-5 pt-16 pb-nav">
        <button
          data-testid="wrapped-back-btn"
          onClick={() => nav(-1)}
          className="flex items-center gap-1 text-sm text-[#FDFBF7]/70 hover:text-[#FF107A]"
        >
          <ArrowLeft size={16} /> Back
        </button>

        <div className="mt-4">
          <div className="flex items-center gap-2 text-[10px] tracking-[0.35em] uppercase font-bold text-[#FFD700]">
            <Sparkles size={12} /> Your Year in Primes
          </div>
          <h1 className="font-display text-5xl leading-[0.9] tracking-tight mt-1">Prime Wrapped</h1>
        </div>

        {empty && (
          <div
            data-testid="wrapped-empty"
            className="mt-6 rounded-2xl border border-white/10 bg-black/30 p-6 text-center"
          >
            <div className="font-display text-2xl">Not enough data yet</div>
            <div className="text-sm text-[#FDFBF7]/60 mt-1">
              Send at least one Prime Challenge and complete it to unlock a richer Wrapped.
            </div>
            <button
              data-testid="wrapped-empty-cta"
              onClick={() => nav("/challenge/new")}
              className="mt-4 rounded-full bg-[#FF107A] text-[#FDFBF7] px-5 py-2 font-bold"
            >
              Send a Challenge
            </button>
          </div>
        )}

        {/* Card preview */}
        <div className="mt-6 flex justify-center">
          <PrimeWrapped ref={cardRef} user={data.user} wrapped={data.wrapped} />
        </div>

        {/* Actions */}
        <div className="mt-6 grid grid-cols-2 gap-3">
          <button
            data-testid="wrapped-download-btn"
            disabled={busy}
            onClick={download}
            className="rounded-full bg-[#FF107A] text-[#FDFBF7] py-3 font-bold flex items-center justify-center gap-2 disabled:opacity-60 glow-pink"
          >
            <Download size={16} /> Download PNG
          </button>
          <button
            data-testid="wrapped-share-btn"
            disabled={busy}
            onClick={share}
            className="rounded-full border border-white/20 bg-black/40 text-[#FDFBF7] py-3 font-bold flex items-center justify-center gap-2 disabled:opacity-60"
          >
            <Share2 size={16} /> Share
          </button>
        </div>

        <button
          data-testid="wrapped-view-leaderboard"
          onClick={() => nav("/leaderboards/prime")}
          className="mt-4 w-full rounded-full border border-[#FFD700]/40 bg-[#FFD700]/10 text-[#FFD700] py-3 font-bold text-sm tracking-wide"
        >
          See where you rank →
        </button>
      </div>
    </div>
  );
}
